import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from "@angular/core";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { Observable, combineLatest, map } from "rxjs";
import { localize } from "@gtmhub/localization";
import { AccountResolverService } from "@gtmhub/state/account-resolver-service";
import { QueryParams } from "@webapp/core/abstracts/models/query-params.model";
import { KpiDetailsState } from "@webapp/kpis/components/kpi-details/kpi-chart/kpi-chart.models";
import { KpiComparisonLabelMap, KpiStatistics, KpiStatisticsCardData } from "@webapp/kpis/components/kpi-details/kpi-statistics/kpi-statistics.models";
import { KpiDetailsStateService } from "@webapp/kpis/components/kpi-details/services/kpi-details-state.service";
import { Kpi, KpiTimeFilter } from "@webapp/kpis/models/kpis.models";
import { MAX_KPI_FRACTION_SIZE } from "@webapp/kpis/utils";
import { ValueFormatOptions, formatValue } from "@webapp/shared/components/formatted-value/utils";
import dayjs from "@webapp/shared/libs/dayjs";
import { ShortNumberPipe } from "@webapp/shared/pipes/short-number/short-number.pipe";
import { CHART_POINT_TOOLTIP_DATE_FORMAT } from "../kpi-chart/utils";
import { KpiStatisticsFacade } from "./services/kpi-statistics-facade.service";
import { comparisonLabelMaps, timeFilterToTimeUnitMap } from "./utils";

@UntilDestroy()
@Component({
  selector: "kpi-statistics-list",
  templateUrl: "./kpi-statistics-list.component.html",
  styleUrls: ["./kpi-statistics-list.component.less"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KpiStatisticsListComponent implements OnInit {
  public statisticList: KpiStatisticsCardData[] = [];
  private comparisonLabelMaps: KpiComparisonLabelMap;

  constructor(
    private kpiDetailsStateService: KpiDetailsStateService,
    private kpiStatisticsFacade: KpiStatisticsFacade,
    private accountResolverService: AccountResolverService,
    private shortNumberPipe: ShortNumberPipe,
    private cdr: ChangeDetectorRef
  ) {}

  public ngOnInit(): void {
    this.getKpiDetailsState();
  }

  public isEmptyState$(): Observable<boolean> {
    return this.kpiDetailsStateService.getChartData$().pipe(map((chartData) => Boolean(chartData && !chartData.snapshots.length && !chartData.projections.length)));
  }

  private getKpiDetailsState(): void {
    this.getKpiDetailsState$()
      .pipe(untilDestroyed(this))
      .subscribe(({ kpi, timeFilter }) => {
        this.setComparisonLabelMaps(timeFilter);
        this.getStatistics(kpi, timeFilter);
      });
  }

  private getKpiDetailsState$(): Observable<KpiDetailsState> {
    return combineLatest({
      kpi: this.kpiDetailsStateService.getKpi$(),
      timeFilter: this.kpiDetailsStateService.getTimeFilter$(),
      snapshots: this.kpiDetailsStateService.getSnapshots$(),
      projections: this.kpiDetailsStateService.getProjections$(),
    });
  }

  private setComparisonLabelMaps(timeFilter: KpiTimeFilter): void {
    const unit = timeFilterToTimeUnitMap[timeFilter] || timeFilter;
    const comparedToDate = dayjs().subtract(1, unit).format(CHART_POINT_TOOLTIP_DATE_FORMAT);

    this.comparisonLabelMaps = {
      ...comparisonLabelMaps[timeFilter],
      previousPeriod: localize("compared_to_date", { date: comparedToDate }),
      nextProjection: localize("to_next_projection"),
    };
  }

  private getStatistics(kpi: Kpi, timeFilter: KpiTimeFilter): void {
    const timeZone = this.accountResolverService.getTimezone();
    const queryParams: QueryParams = { type: timeFilter, timeZone };

    this.kpiStatisticsFacade
      .getStatistics$(kpi.id, queryParams)
      .pipe(untilDestroyed(this))
      .subscribe((statistics) => {
        this.cdr.detach();
        this.setStatisticList(statistics, kpi);
        this.cdr.detectChanges();
      });
  }

  private setStatisticList(statistics: KpiStatistics, kpi: Kpi): void {
    const { targetOperator, formatting } = kpi;

    const lessIsBetter = targetOperator === "should_decrease";
    this.statisticList = Object.keys(statistics).map((key) => {
      const statistic = statistics[key];
      const value = this.formatStatisticValue(statistic.value, formatting.fractionSize);
      const comparisonLabel = this.comparisonLabelMaps[key];
      const progressIndicatorData = {
        lessIsBetter,
        deltaPercentage: statistic.deltaPercentage,
      };
      return {
        value,
        comparisonLabel,
        progressIndicatorData,
      };
    });
  }

  private formatStatisticValue(value: number, fractionSize: number, maxFractionDigits = MAX_KPI_FRACTION_SIZE): string {
    const prefix = value > 0 ? "+" : "";
    const options: ValueFormatOptions = { fractionSize, maxFractionDigits };
    const statisticValue = formatValue(value, this.shortNumberPipe, options);

    return `${prefix}${statisticValue}`;
  }
}
